import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { SettingsProvider } from '@/contexts/SettingsContext';
import LandingPage from '@/pages/LandingPage';
import AuthPage from '@/pages/AuthPage';
import VerifyEmailPage from '@/pages/VerifyEmailPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import Dashboard from '@/pages/Dashboard';
import ContactsPage from '@/pages/ContactsPage';
import BookingsPage from '@/pages/BookingsPage';
import MessagesPage from '@/pages/MessagesPage';
import TasksPage from '@/pages/TasksPage';
import TaskSettingsPage from '@/pages/TaskSettingsPage';
import EmployeesPage from '@/pages/EmployeesPage';
import PackagesPage from '@/pages/PackagesPage';
import EventTypesPage from '@/pages/EventTypesPage';
import VendorsVenuesPage from '@/pages/VendorsVenuesPage';
import ContractsPage from '@/pages/ContractsPage';
import ContractTemplatesPage from '@/pages/ContractTemplatesPage';
import InvoiceTemplatesPage from '@/pages/InvoiceTemplatesPage';
import InvoicesPage from '@/pages/InvoicesPage';
import AccountsPage from '@/pages/AccountsPage';
import VenueTypesPage from '@/pages/VenueTypesPage';
import FormsPage from '@/pages/FormsPage';
import DiscountsPage from '@/pages/DiscountsPage';
import EmailTemplatesPage from '@/pages/EmailTemplatesPage';
import SettingsPage from '@/pages/SettingsPage';
import RolesPage from '@/pages/RolesPage';
import AvailabilitySettingsPage from '@/pages/AvailabilitySettingsPage';
import WidgetsPage from '@/pages/WidgetsPage';
import PlatformAccountsPage from '@/pages/PlatformAccountsPage';
import PlatformDataExportPage from '@/pages/PlatformDataExportPage';
import FrontendSettingsPage from '@/pages/FrontendSettingsPage';
import SupportFeaturesPage from '@/pages/SupportFeaturesPage';
import SupportBugsPage from '@/pages/SupportBugsPage';
import SupportFeatureRequestsPage from '@/pages/SupportFeatureRequestsPage';
import SupportCategoriesPage from '@/pages/SupportCategoriesPage';
import PaymentRequiredScreen from '@/components/PaymentRequiredScreen';
import ClientPortal from '@/pages/ClientPortalV2';
import GuestRequestsPage from '@/pages/GuestRequestsPage';
import GuestExperiencePage from '@/pages/GuestExperiencePage';
import DJLiveDashboard from '@/pages/DJLiveDashboard';
import EventSlideshowPage from '@/pages/EventSlideshowPage';
import EventGalleryPage from '@/pages/EventGalleryPage';
import EquipmentPage from '@/pages/EquipmentPage';
import EmailCampaignsPage from '@/pages/EmailCampaignsPage';
import MusicLibraryPage from '@/pages/MusicLibraryPage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import WidgetAvailability from '@/pages/WidgetAvailability';
import WidgetContact from '@/pages/WidgetContact';
import WidgetLogin from '@/pages/WidgetLogin';
import WebsiteBuilderPage from '@/pages/WebsiteBuilderPage';
import PublicWebsite from '@/pages/PublicWebsite';
import PlatformSettingsPage from '@/pages/PlatformSettingsPage';
import PublicHomePage from '@/pages/PublicHomePage';
import PublicFeaturesPage from '@/pages/PublicFeaturesPage';
import PublicFAQPage from '@/pages/PublicFAQPage';
import PublicContactPage from '@/pages/PublicContactPage';
import PublicFreeTrialPage from '@/pages/PublicFreeTrialPage';
import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEmployee, setIsEmployee] = useState(false);
  const [employeeInfo, setEmployeeInfo] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [requiresPayment, setRequiresPayment] = useState(false);

  useEffect(() => {
    // Check for impersonate token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const impersonateToken = urlParams.get('impersonate');
    const impersonateUser = urlParams.get('user');
    
    if (impersonateToken && impersonateUser) {
      try {
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Set the impersonation session
        const userData = JSON.parse(decodeURIComponent(impersonateUser));
        localStorage.setItem('token', impersonateToken);
        localStorage.setItem('impersonating', 'true');
        axios.defaults.headers.common['Authorization'] = `Bearer ${impersonateToken}`;
        setUser(userData);
        setLoading(false);
        return;
      } catch (e) {
        console.error('Failed to parse impersonation data:', e);
      }
    }
    
    const token = localStorage.getItem('token');
    const storedIsEmployee = localStorage.getItem('isEmployee') === 'true';
    const storedPermissions = localStorage.getItem('permissions');
    const storedEmployeeInfo = localStorage.getItem('employeeInfo');
    
    if (storedIsEmployee) {
      setIsEmployee(true);
      if (storedPermissions) setPermissions(JSON.parse(storedPermissions));
      if (storedEmployeeInfo) setEmployeeInfo(JSON.parse(storedEmployeeInfo));
    }
    
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  // Check payment status whenever user changes
  useEffect(() => {
    if (user && !isEmployee) {
      checkPaymentStatus();
    }
  }, [user, isEmployee]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/account/payment-status`);
      setPaymentStatus(response.data);
      setRequiresPayment(response.data.requires_payment === true);
    } catch (error) {
      console.error('Payment status check error:', error);
      // Don't block on error - assume no payment required
      setRequiresPayment(false);
    }
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Auth error:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('isEmployee');
      localStorage.removeItem('permissions');
      localStorage.removeItem('employeeInfo');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (token, userData, employeeData = null, perms = null) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    
    if (employeeData) {
      setIsEmployee(true);
      setEmployeeInfo(employeeData);
      setPermissions(perms);
      localStorage.setItem('isEmployee', 'true');
      localStorage.setItem('employeeInfo', JSON.stringify(employeeData));
      if (perms) localStorage.setItem('permissions', JSON.stringify(perms));
    } else {
      setIsEmployee(false);
      setEmployeeInfo(null);
      setPermissions(null);
      localStorage.removeItem('isEmployee');
      localStorage.removeItem('employeeInfo');
      localStorage.removeItem('permissions');
    }
    
    // Dispatch auth change event for SettingsContext to pick up
    window.dispatchEvent(new Event('authChange'));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isEmployee');
    localStorage.removeItem('permissions');
    localStorage.removeItem('employeeInfo');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setIsEmployee(false);
    setEmployeeInfo(null);
    setPermissions(null);
    setPaymentStatus(null);
    setRequiresPayment(false);
    
    // Dispatch auth change event for SettingsContext to pick up
    window.dispatchEvent(new Event('authChange'));
  };

  // Helper to check if user has permission
  const hasPermission = (permission) => {
    if (!isEmployee) return true; // Owner has all permissions
    if (!permissions) return false;
    if (permissions.administrator) return true;
    return permissions[permission] === true;
  };

  if (loading) {
    return (
      <ThemeProvider>
        <div className="min-h-screen bg-obsidian flex items-center justify-center">
          <div className="text-neon-blue text-xl font-heading">Loading...</div>
        </div>
      </ThemeProvider>
    );
  }

  // Show payment required screen if user is logged in and payment is required
  // Only for company owners (not employees)
  if (user && requiresPayment && !isEmployee) {
    return (
      <ThemeProvider>
        <PaymentRequiredScreen 
          user={user}
          paymentStatus={paymentStatus}
          onLogout={handleLogout}
          onPaymentComplete={() => {
            checkPaymentStatus();
          }}
        />
        <Toaster position="top-right" />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <SettingsProvider>
        <BrowserRouter>
          <Routes>
          <Route path="/" element={<PublicHomePage />} />
          <Route path="/features" element={<PublicFeaturesPage />} />
          <Route path="/faq" element={<PublicFAQPage />} />
          <Route path="/contact" element={<PublicContactPage />} />
          <Route path="/free-trial" element={<PublicFreeTrialPage />} />
          <Route 
            path="/auth" 
            element={user ? <Navigate to="/dashboard" /> : <AuthPage onLogin={handleLogin} />} 
          />
          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route 
            path="/dashboard" 
            element={user ? <Dashboard user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/contacts" 
            element={user ? <ContactsPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/bookings" 
            element={user ? <BookingsPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/messages" 
            element={user ? <MessagesPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/tasks" 
            element={user ? <TasksPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/employees" 
            element={user ? <EmployeesPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/configuration/packages" 
            element={user ? <PackagesPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/configuration/event-types" 
            element={user ? <EventTypesPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/configuration/venue-types" 
            element={user ? <VenueTypesPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/configuration/task-settings" 
            element={user ? <TaskSettingsPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/contracts" 
            element={user ? <ContractsPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/configuration/contract-templates" 
            element={user ? <ContractTemplatesPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/configuration/invoice-templates" 
            element={user ? <InvoiceTemplatesPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/configuration/forms" 
            element={user ? <FormsPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/configuration/discounts" 
            element={user ? <DiscountsPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/configuration/email-templates" 
            element={user ? <EmailTemplatesPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/invoices" 
            element={user ? <InvoicesPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/accounts" 
            element={user ? <AccountsPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/vendors-venues" 
            element={user ? <VendorsVenuesPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/equipment" 
            element={user ? <EquipmentPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/email-campaigns" 
            element={user ? <EmailCampaignsPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/music-library" 
            element={user ? <MusicLibraryPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/widgets" 
            element={user ? <WidgetsPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/analytics" 
            element={user ? <AnalyticsPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/platform-accounts" 
            element={user?.is_platform_admin ? <PlatformAccountsPage user={user} onLogout={handleLogout} /> : <Navigate to="/dashboard" />} 
          />
          <Route 
            path="/platform-settings" 
            element={user?.is_platform_admin ? <PlatformSettingsPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/dashboard" />} 
          />
          <Route 
            path="/platform-export" 
            element={user?.is_platform_admin ? <PlatformDataExportPage user={user} onLogout={handleLogout} /> : <Navigate to="/dashboard" />} 
          />
          <Route 
            path="/frontend-settings" 
            element={user?.is_platform_admin ? <FrontendSettingsPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/dashboard" />} 
          />
          <Route 
            path="/settings" 
            element={user ? <SettingsPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/roles" 
            element={user ? <RolesPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/availability" 
            element={user ? <AvailabilitySettingsPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          {/* Support Routes */}
          <Route 
            path="/support/features" 
            element={user ? <SupportFeaturesPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/support/bugs" 
            element={user ? <SupportBugsPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/support/feature-requests" 
            element={user ? <SupportFeatureRequestsPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/support/categories" 
            element={user ? <SupportCategoriesPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/website-builder" 
            element={user ? <WebsiteBuilderPage user={user} onLogout={handleLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} /> : <Navigate to="/auth" />} 
          />
          <Route path="/site/:slug" element={<PublicWebsite />} />
          <Route path="/portal/:token" element={<ClientPortal />} />
          <Route path="/guest-requests/:linkCode" element={<GuestRequestsPage />} />
          <Route path="/event/:eventCode" element={<GuestExperiencePage />} />
          <Route path="/event/:eventCode/slideshow" element={<EventSlideshowPage />} />
          <Route path="/event/:eventCode/gallery" element={<EventGalleryPage />} />
          <Route 
            path="/dj-live/:bookingId" 
            element={user ? <DJLiveDashboard /> : <Navigate to="/auth" />} 
          />
          {/* Public Widget Routes */}
          <Route path="/widget/availability/:userId" element={<WidgetAvailability />} />
          <Route path="/widget/contact/:userId" element={<WidgetContact />} />
          <Route path="/widget/login/:userId" element={<WidgetLogin />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" theme="dark" />
      </SettingsProvider>
    </ThemeProvider>
  );
}

export default App;